import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiCamera, FiBarChart, FiPackage, FiPlus, FiAlertCircle, FiTruck, FiInfo, FiDollarSign, FiTag, FiStar } from 'react-icons/fi';
import { useProducto } from '../../hooks/useProductos';
import { proveedoresService, clientCategoriesService, formulacionesService, productosService, clientesService } from '../../services';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import ProductImageGallery from '../../components/UI/ProductImageGallery';
import FormulacionFormSimple from '../../components/Productos/FormulacionFormSimple';
import DuplicarFormulacionModal from '../../components/Productos/DuplicarFormulacionModal';

const ProductoForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const {
    producto,
    isEditing,
    isLoading,
    error,
    hasChanges,
    updateProductoData,
    saveProducto,
    resetProducto,
    clearError
  } = useProducto(id);

  const [precios, setPrecios] = useState([
    { tipo: 'tienda', nombre: 'Precio Tienda', valor: '' },
    { tipo: 'distribuidor', nombre: 'Precio Distribuidor', valor: '' }
  ]);
  const [validationErrors, setValidationErrors] = useState({});
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para tabs
  const [activeTab, setActiveTab] = useState('datos');
  const [successMessage, setSuccessMessage] = useState(null);

  // Estado para formulación
  const [formulacionData, setFormulacionData] = useState(null);
  const [originalFormulacionData, setOriginalFormulacionData] = useState(null);
  const [hasFormulationChanges, setHasFormulationChanges] = useState(false);
  const [hasImageChanges, setHasImageChanges] = useState(false);
  const [pendingImages, setPendingImages] = useState([]); // imágenes para subir al crear
  const [isDuplicarModalOpen, setIsDuplicarModalOpen] = useState(false);

  // Debug: Log cuando cambia formulacionData
  useEffect(() => {
    console.log('🔄 formulacionData cambió:', formulacionData);
  }, [formulacionData]);

  // Actualizar company_name y formula_type en la fórmula cuando cambia product_type
  useEffect(() => {
    if (formulacionData && producto.product_type) {
      const updatedData = { ...formulacionData };
      let shouldUpdate = false;

      // Mapear product_type a company_name y formula_type
      if (producto.product_type === 'BETEL') {
        if (updatedData.company_name !== 'BETEL NATURAL' || updatedData.formula_type !== 'BETEL') {
          updatedData.company_name = 'BETEL NATURAL';
          updatedData.formula_type = 'BETEL';
          shouldUpdate = true;
        }
      } else if (producto.product_type === 'NATURALANDLAB') {
        if (updatedData.company_name !== 'NATURALAND LAB' || updatedData.formula_type !== 'NATURALANDLAB') {
          updatedData.company_name = 'NATURALAND LAB';
          updatedData.formula_type = 'NATURALANDLAB';
          shouldUpdate = true;
        }
      } else if (producto.product_type === 'COTIZACIONES') {
        if (updatedData.company_name !== 'COTIZACIONES' || updatedData.formula_type !== 'COTIZACIONES') {
          updatedData.company_name = 'COTIZACIONES';
          updatedData.formula_type = 'COTIZACIONES';
          shouldUpdate = true;
        }
      }

      if (shouldUpdate) {
        console.log('✅ Actualizando fórmula automáticamente por cambio de departamento:', {
          product_type: producto.product_type,
          company_name: updatedData.company_name,
          formula_type: updatedData.formula_type
        });
        setFormulacionData(updatedData);
      }
    }
  }, [producto.product_type]);

  // Estado para proveedores cargados desde API
  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(true);

  // Estado para categorías de clientes cargadas desde API
  const [clientCategories, setClientCategories] = useState([]);
  const [loadingClientCategories, setLoadingClientCategories] = useState(true);

  // Estado para clientes NaturalAndLab cargados desde API
  const [clientesNaturalAndLab, setClientesNaturalAndLab] = useState([]);
  const [loadingClientesNaturalAndLab, setLoadingClientesNaturalAndLab] = useState(false);

  // Opciones para los selectores según el documento PDF
  const tiposProducto = [
    { value: 'BETEL', label: 'Betel Natural' },
    { value: 'NATURALANDLAB', label: 'Naturaland Lab' },
    { value: 'COTIZACIONES', label: 'Cotizaciones' },
    { value: 'OTHER_BRANDS', label: 'Otras Marcas' },
    { value: 'INGREDIENT', label: 'Ingrediente' },
    { value: 'PACKAGING_MATERIAL', label: 'Material de Empaque' }
  ];

  const estados = [
    { value: 'ACTIVE', label: 'Activo' },
    { value: 'INACTIVE', label: 'Inactivo' },
    { value: 'DISCONTINUED', label: 'Descontinuado' },
    { value: 'OUT_OF_STOCK', label: 'Agotado' }
  ];

  const categorias = [
    { value: 'CAPSULES', label: 'Cápsulas' },
    { value: 'POWDERS', label: 'Polvos' },
    { value: 'LIQUIDS', label: 'Líquidos' },
    { value: 'OILS', label: 'Aceites' },
    { value: 'TEAS', label: 'Infusiones' },
    { value: 'CREAMS', label: 'Cremas' },
    { value: 'LEAVES', label: 'Hojas' }
  ];

  const unidadesMedida = [
    // Peso
    { value: 'KG',      label: 'Kilogramo (kg)',       group: 'Peso' },
    { value: 'G',       label: 'Gramo (g)',             group: 'Peso' },
    { value: 'MG',      label: 'Miligramo (mg)',        group: 'Peso' },
    { value: 'MCG',     label: 'Microgramo (mcg)',      group: 'Peso' },
    // Volumen
    { value: 'L',       label: 'Litro (l)',             group: 'Volumen' },
    { value: 'ML',      label: 'Mililitro (ml)',        group: 'Volumen' },
    // Conteo
    { value: 'UNIT',    label: 'Unidad',                group: 'Conteo' },
    { value: 'BOTTLE',  label: 'Botella',               group: 'Conteo' },
    { value: 'BOX',     label: 'Caja',                  group: 'Conteo' },
    { value: 'PACKAGE', label: 'Paquete',               group: 'Conteo' },
  ];



  const handleInputChange = (field, value) => {
    updateProductoData(field, value);

    // Limpiar error de validación del campo específico
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Lógica especial para departamento
    if (field === 'product_type') {
      if (value === 'NATURALANDLAB' && !producto.naturalandlab_client_id) {
        updateProductoData('naturalandlab_client_id', '');
      }
    }
  };

  const handlePrecioChange = (index, field, value) => {
    const nuevosPrecios = [...precios];
    nuevosPrecios[index][field] = value;
    setPrecios(nuevosPrecios);

    // Convertir precios a formato category_prices para el backend
    const categoryPrices = nuevosPrecios
      .filter(p => p.valor && parseFloat(p.valor) > 0)
      .map(p => ({
        category_code: p.category_code,
        price: parseFloat(p.valor)
      }));

    console.log('💵 Actualizando category_prices:', categoryPrices);
    updateProductoData('category_prices', categoryPrices);
  };

  // Ya no necesitamos agregar/eliminar precios dinámicamente
  // Los precios se basan en las categorías de clientes que existen en la BD

  // Funciones de generación de códigos movidas a handleSubmit para generación automática

  const validateForm = () => {
    const newErrors = {};

    // Validaciones requeridas según la maqueta
    if (!producto.name || !producto.name.trim()) {
      newErrors.name = 'El nombre del producto es requerido';
    }

    if (!producto.description || !producto.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    }

    // cost_price es requerido SOLO si no tiene fórmula (la fórmula lo calcula automáticamente)
    const hasFormula = formulacionData && formulacionData.ingredients && formulacionData.ingredients.length > 0;
    if (!hasFormula && (!producto.cost_price || parseFloat(producto.cost_price) <= 0)) {
      newErrors.cost_price = 'El costo es requerido y debe ser mayor a 0';
    }

    // main_supplier_id es opcional, no se valida

    if (!producto.category) {
      newErrors.category = 'La categoría es requerida';
    }

    // Para ingredientes, la unidad debe ser de peso o volumen (no conteo genérico)
    if (producto.product_type === 'INGREDIENT') {
      const countUnits = ['UNIT', 'BOTTLE', 'BOX', 'PACKAGE'];
      if (!producto.unit || countUnits.includes(producto.unit)) {
        newErrors.unit = 'Para ingredientes seleccione una unidad de peso (kg, g, mg, mcg) o volumen (l, ml)';
      }
    }

    // Validar al menos un precio válido
    const preciosValidos = precios.filter(p => p.valor && parseFloat(p.valor) > 0);
    if (preciosValidos.length === 0) {
      newErrors.precios = 'Debe establecer al menos un precio';
    } else {
      // Validar que el precio más alto sea mayor al costo (backend requiere sale_price > cost_price)
      const costPrice = parseFloat(producto.cost_price) || 0;
      if (costPrice > 0) {
        const precioMaximo = Math.max(...preciosValidos.map(p => parseFloat(p.valor)));
        if (precioMaximo <= costPrice) {
          newErrors.precios = `Al menos un precio debe ser mayor al costo del producto ($${costPrice.toFixed(2)})`;
        }
      }
    }

    // Validar max_stock > min_stock
    const minStock = parseFloat(producto.min_stock);
    const maxStock = parseFloat(producto.max_stock);
    if (producto.max_stock !== '' && producto.max_stock !== null && producto.max_stock !== undefined &&
      !isNaN(maxStock) && !isNaN(minStock) && maxStock < minStock) {
      newErrors.max_stock = `El stock máximo (${maxStock}) debe ser mayor al stock mínimo (${minStock})`;
    }

    // Si es NaturalAndLab, requiere cliente
    if (producto.product_type === 'NATURALANDLAB' && !producto.naturalandlab_client_id) {
      newErrors.naturalandlab_client_id = 'Debe seleccionar el cliente para productos NaturalAndLab';
    }

    // Validar precio de oferta si está en oferta
    if (producto.is_on_sale) {
      if (!producto.sale_discount_price || parseFloat(producto.sale_discount_price) <= 0) {
        newErrors.sale_discount_price = 'Debe establecer un precio con descuento';
      } else if (parseFloat(producto.sale_discount_price) >= parseFloat(producto.sale_price)) {
        newErrors.sale_discount_price = 'El precio con descuento debe ser menor al precio regular';
      }
    }

    // Validar campos de formulación si hay ingredientes
    if (formulacionData && formulacionData.ingredients && formulacionData.ingredients.length > 0) {
      if (!formulacionData.formula_name || !formulacionData.formula_name.trim()) {
        newErrors.formula_name = 'El nombre de la formulación es requerido cuando hay ingredientes';
      }

      if (!formulacionData.quantity_per_container || parseFloat(formulacionData.quantity_per_container) <= 0) {
        newErrors.quantity_per_container = 'La cantidad por envase es requerida cuando hay ingredientes';
      }
    }

    setValidationErrors(newErrors);
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm();
    if (!validation.isValid) {
      // Si hay errores de formulación, cambiar a esa tab
      if (validation.errors.formula_name || validation.errors.quantity_per_container) {
        setActiveTab('formulacion');
      }
      return;
    }

    setIsSaving(true);
    setValidationErrors({});

    try {
      // El hook useProducto se encarga de:
      // - Generar código del producto si está vacío
      // - Generar código de barras si está vacío
      // - Calcular sale_price desde category_prices si es necesario
      console.log('Submitting product with category_prices:', producto.category_prices);

      // Agregar datos completos de formulación al producto si existen
      console.log('🔍 Verificando formulacionData:', formulacionData);
      console.log('   - ¿Existe formulacionData?', !!formulacionData);
      console.log('   - ¿Tiene ingredients?', formulacionData?.ingredients);
      console.log('   - ¿Cantidad de ingredientes?', formulacionData?.ingredients?.length);
      console.log('   - Datos completos:', JSON.stringify(formulacionData, null, 2));

      // Preparar override data con toda la información de la fórmula
      const overrideData = {};

      // IMPORTANTE: Si hay formulacionData, incluirlo
      if (formulacionData) {
        // Si hay formulacionData, agregar TODOS los campos de la fórmula
        console.log('📋 Agregando formulación completa al producto');

        // Ingredientes
        if (formulacionData.ingredients && formulacionData.ingredients.length > 0) {
          overrideData.formulation_ingredients = formulacionData.ingredients.map(ing => ({
            ingredient_id: parseInt(ing.ingredient_id),
            ingredient_name: ing.ingredient_name,
            quantity: parseFloat(ing.quantity_per_unit),
            unit: ing.unit,
            notes: null
          }));
        } else {
          overrideData.formulation_ingredients = null;
        }

        // Metadatos completos de la fórmula
        overrideData.formula_data = {
          company_name: formulacionData.company_name || 'BETEL NATURAL',
          formula_type: formulacionData.formula_type || 'BETEL',
          formula_name: formulacionData.formula_name || '',  // NO usar producto.name como fallback
          container_product_id: formulacionData.container_product_id ? parseInt(formulacionData.container_product_id) : null,
          lid_product_id: formulacionData.lid_product_id ? parseInt(formulacionData.lid_product_id) : null,
          quantity_per_container: formulacionData.quantity_per_container ? parseFloat(formulacionData.quantity_per_container) : null,
          quantity_unit: formulacionData.quantity_unit || 'UNIT',
          capsule_type: formulacionData.capsule_type || 'VEGETARIAN',
          mixing_time_minutes: formulacionData.mixing_time_minutes ? parseInt(formulacionData.mixing_time_minutes) : null,
          labor_cost_per_hour: formulacionData.labor_cost_per_hour ? parseFloat(formulacionData.labor_cost_per_hour) : null,
          best_use_date_years: formulacionData.best_use_date_years || null,
          label_printer: formulacionData.label_printer || null,
          box_size: formulacionData.box_size || null,
          pieces_per_box: formulacionData.pieces_per_box ? parseInt(formulacionData.pieces_per_box) : null,
          includes_silica: formulacionData.includes_silica || false,
          production_quantities: formulacionData.production_quantities || []
        };

        console.log('📦 Formula data a enviar:', overrideData.formula_data);
      } else {
        // Si no hay formulación, enviar null
        overrideData.formulation_ingredients = null;
        overrideData.formula_data = null;
      }

      console.log('📦 Override data completo a enviar:', overrideData);

      // Pasar overrideData a saveProducto para incluir formulation_ingredients
      const result = await saveProducto(overrideData);

      if (result.success) {
        const productId = result.data?.id;

        // Mensaje de éxito
        const hasFormulation = formulacionData && formulacionData.ingredients && formulacionData.ingredients.length > 0;
        let successMsg;

        if (hasFormulation) {
          successMsg = isEditing
            ? 'Producto y formulación actualizados exitosamente'
            : 'Producto y formulación creados exitosamente';
        } else {
          successMsg = result.message || (isEditing ? 'Producto actualizado exitosamente' : 'Producto guardado exitosamente');
        }

        setSuccessMessage(successMsg);
        console.log(`✅ Producto ${isEditing ? 'actualizado' : 'creado'} exitosamente con ID:`, productId);

        if (hasFormulation) {
          console.log(`✅ Formulación ${isEditing ? 'actualizada' : 'creada'} con`, formulacionData.ingredients.length, 'ingredientes');
        }

        // Si es creación y hay imágenes pendientes, subirlas antes de navegar
        if (!isEditing && productId && pendingImages.length > 0) {
          for (let i = 0; i < pendingImages.length; i++) {
            try {
              await productosService.uploadProductImage(productId, pendingImages[i], {
                alt_text: 'Imagen de producto',
                is_primary: i === 0,
                display_order: i
              });
            } catch (imgErr) {
              console.error('Error subiendo imagen pendiente:', imgErr);
            }
          }
          setPendingImages([]);
        }

        // Navegar según el caso
        if (!isEditing && productId) {
          // Volver a la lista de productos
          navigate('/system/productos', {
            state: {
              message: successMsg,
              type: 'success'
            }
          });
        } else {
          // Si es edición, mostrar mensaje y auto-ocultar después de 5 segundos
          // Actualizar el estado original de la formulación para reflejar los cambios guardados
          if (formulacionData) {
            setOriginalFormulacionData(JSON.parse(JSON.stringify(formulacionData)));
            setHasFormulationChanges(false);
          }
          setHasImageChanges(false);
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      } else {
        // Procesar errores de validación
        console.log('Result completo:', result);
        console.log('Result.detail:', result.detail);
        const processedErrors = {};

        // Mapeo de campos a español
        const fieldLabels = {
          name: 'Nombre del Producto',
          description: 'Descripción',
          code: 'Código del Producto',
          barcode: 'UPC',
          product_type: 'Tipo de Producto',
          category: 'Categoría',
          status: 'Estado',
          cost_price: 'Costo del Producto',
          sale_price: 'Precio de Venta',
          wholesale_price: 'Precio Mayorista',
          current_stock: 'Stock Actual',
          min_stock: 'Stock Mínimo',
          max_stock: 'Stock Máximo',
          brand: 'Marca',
          main_supplier_id: 'Proveedor Principal'
        };

        // Procesar errores de FastAPI (formato detail)
        if (result.detail) {
          // Si detail es un string (mensaje de error directo)
          if (typeof result.detail === 'string') {
            // Si el error menciona formula_name, marcarlo en ese campo
            if (result.detail.includes('nombre de la fórmula') || result.detail.includes('formula_name')) {
              processedErrors.formula_name = result.detail;
              // Cambiar a la tab de formulación
              setActiveTab('formulacion');
            } else {
              processedErrors.general = result.detail;
            }
          }
          // Si detail es un array (errores de validación múltiples)
          else if (Array.isArray(result.detail)) {
            result.detail.forEach(err => {
              const field = err.loc && err.loc.length > 1 ? err.loc[err.loc.length - 1] : 'general';
              const fieldLabel = fieldLabels[field] || field;

              // Traducir mensajes comunes
              let message = err.msg;
              if (err.type === 'greater_than') {
                message = `${fieldLabel} debe ser mayor a ${err.ctx?.gt || 0}`;
              } else if (err.type === 'missing') {
                message = `${fieldLabel} es requerido`;
              } else if (err.type === 'value_error') {
                // Traducir errores de valor específicos
                if (err.msg.includes('Sale price should be greater than cost price')) {
                  message = 'El precio de venta debe ser mayor al costo. Verifique que al menos uno de los precios por categoría sea mayor al costo del producto.';
                } else if (err.msg.includes('Wholesale price should be greater than cost price')) {
                  message = 'El precio mayorista debe ser mayor al costo del producto';
                } else if (err.msg.includes('Max stock must be greater than min stock')) {
                  message = 'El stock máximo debe ser mayor al stock mínimo';
                } else {
                  message = `${fieldLabel}: ${err.msg}`;
                }
              } else {
                message = `${fieldLabel}: ${err.msg}`;
              }

              processedErrors[field] = message;
            });
          }
        }

        // Procesar validation_errors (si vienen en formato alternativo)
        if (result.validation_errors && Array.isArray(result.validation_errors)) {
          result.validation_errors.forEach(err => {
            // Si el error no fue procesado por detail, traducirlo aquí
            if (!processedErrors[err.field]) {
              let translatedMessage = err.message;
              if (err.message.includes('Sale price should be greater than cost price')) {
                translatedMessage = 'El precio de venta debe ser mayor al costo. Verifique que al menos uno de los precios por categoría sea mayor al costo del producto.';
              } else if (err.message.includes('Wholesale price should be greater than cost price')) {
                translatedMessage = 'El precio mayorista debe ser mayor al costo del producto';
              } else if (err.message.includes('Max stock must be greater than min stock')) {
                translatedMessage = 'El stock máximo debe ser mayor al stock mínimo';
              }
              processedErrors[err.field] = translatedMessage;
            }
          });
        }

        if (result.validationErrors && typeof result.validationErrors === 'object') {
          Object.keys(result.validationErrors).forEach(field => {
            // Si el error no fue procesado aún, traducirlo
            if (!processedErrors[field]) {
              let translatedMessage = result.validationErrors[field];
              if (translatedMessage.includes('Sale price should be greater than cost price')) {
                translatedMessage = 'El precio de venta debe ser mayor al costo. Verifique que al menos uno de los precios por categoría sea mayor al costo del producto.';
              } else if (translatedMessage.includes('Wholesale price should be greater than cost price')) {
                translatedMessage = 'El precio mayorista debe ser mayor al costo del producto';
              } else if (translatedMessage.includes('Max stock must be greater than min stock')) {
                translatedMessage = 'El stock máximo debe ser mayor al stock mínimo';
              }
              processedErrors[field] = translatedMessage;
            }
          });
        }

        // Si no hay errores procesados, usar error o message
        if (Object.keys(processedErrors).length === 0) {
          if (result.error) {
            processedErrors.general = result.error;
          } else if (result.message) {
            // Traducir mensajes comunes del backend
            let errorMessage = result.message;

            // Error de código de barras duplicado
            if (errorMessage.includes('duplicate key value violates unique constraint "products_barcode_key"') ||
              errorMessage.includes('barcode') && errorMessage.includes('already exists')) {
              processedErrors.barcode = 'Este código de barras ya existe. Por favor, use uno diferente o déjelo vacío para generar uno automático.';
            }
            // Error de código duplicado
            else if (errorMessage.includes('duplicate key value violates unique constraint "products_code_key"') ||
              errorMessage.includes('code') && errorMessage.includes('already exists')) {
              processedErrors.code = 'Este código de producto ya existe. Por favor, use uno diferente.';
            }
            // Error de nombre duplicado
            else if (errorMessage.includes('duplicate key value violates unique constraint "products_name_key"') ||
              errorMessage.includes('name') && errorMessage.includes('already exists')) {
              processedErrors.name = 'Este nombre de producto ya existe. Por favor, use uno diferente.';
            }
            // Otros errores generales
            else {
              processedErrors.general = errorMessage;
            }
          }
        }

        setValidationErrors(processedErrors);
      }
    } catch (err) {
      setValidationErrors({
        general: 'Error inesperado al guardar el producto'
      });
      console.error('Unexpected error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges || hasFormulationChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      navigate('/system/productos');
    }
  };

  const confirmCancel = () => {
    resetProducto();
    navigate('/system/productos');
  };

  // Efectos para sincronizar precios cuando se carga el producto
  // Inicializar precios basados en category_prices del producto y categorías disponibles
  useEffect(() => {
    if (clientCategories.length > 0) {
      console.log('💰 Sincronizando precios por categoría');
      console.log('   - Categorías disponibles:', clientCategories);
      console.log('   - Precios del producto:', producto.category_prices);

      // Si el producto tiene category_prices, usarlos
      if (producto.category_prices && producto.category_prices.length > 0) {
        console.log('✅ Producto tiene precios guardados, cargándolos...');
        const preciosIniciales = clientCategories.map(cat => {
          const precioExistente = producto.category_prices.find(
            cp => cp.category_code === cat.code
          );
          return {
            category_code: cat.code,
            nombre: cat.name,
            valor: precioExistente ? precioExistente.price.toString() : ''
          };
        });
        console.log('   - Precios inicializados:', preciosIniciales);
        setPrecios(preciosIniciales);
      } else {
        console.log('ℹ️ Producto nuevo o sin precios, inicializando vacío');
        // Producto nuevo o sin precios por categoría, inicializar vacío
        const preciosVacios = clientCategories.map(cat => ({
          category_code: cat.code,
          nombre: cat.name,
          valor: ''
        }));
        setPrecios(preciosVacios);
      }
    }
  }, [clientCategories, producto.category_prices]);

  // Limpiar errores cuando se monta el componente
  useEffect(() => {
    clearError();
    setValidationErrors({});
  }, [clearError]);

  // Detectar si debe cambiar al tab de formulación después de crear
  useEffect(() => {
    if (location.state?.switchToFormulation) {
      setActiveTab('formulacion');
      setSuccessMessage(location.state.message);
      // Limpiar el state para que no se active de nuevo
      window.history.replaceState({}, document.title);
      // Auto-ocultar mensaje después de 8 segundos
      setTimeout(() => setSuccessMessage(null), 8000);
    }
  }, [location.state]);

  // Cargar categorías de clientes desde API
  useEffect(() => {
    const loadClientCategories = async () => {
      setLoadingClientCategories(true);
      try {
        const result = await clientCategoriesService.getCategorias();

        console.log('Client categories result:', result);

        if (result.success) {
          // El API devuelve { categories: [], total: N }, no { data: [] }
          const allCategories = result.data?.categories || result.data || [];
          // Filtrar solo categorías activas
          const categoriasActivas = allCategories.filter(cat => cat.is_active);
          console.log('Active client categories loaded:', categoriasActivas);
          setClientCategories(categoriasActivas);
        } else {
          console.error('Error al cargar categorías de clientes:', result.message);
          setClientCategories([]);
        }
      } catch (err) {
        console.error('Error loading client categories:', err);
        setClientCategories([]);
      } finally {
        setLoadingClientCategories(false);
      }
    };

    loadClientCategories();
  }, []);

  // Cargar proveedores desde API
  useEffect(() => {
    const loadProveedores = async () => {
      setLoadingProveedores(true);
      try {
        const result = await proveedoresService.getProveedores();

        if (result.success) {
          const proveedoresTransformados = (result.data || []).map(proveedor => ({
            value: proveedor.id.toString(),
            label: proveedor.name || `Proveedor ${proveedor.id}`
          }));
          setProveedores(proveedoresTransformados);
        } else {
          console.error('Error al cargar proveedores:', result.message);
          setProveedores([]);
        }
      } catch (err) {
        console.error('Error loading proveedores:', err);
        setProveedores([]);
      } finally {
        setLoadingProveedores(false);
      }
    };

    loadProveedores();
  }, []);

  // Cargar clientes NaturalAndLab desde API
  useEffect(() => {
    const loadClientesNaturalAndLab = async () => {
      setLoadingClientesNaturalAndLab(true);
      try {
        // Llamadas paralelas: clientes naturalandlab + clientes both
        const [resNal, resBoth] = await Promise.all([
          clientesService.getClientes({ business_type: 'NATURALANDLAB', is_active: true, limit: 500 }),
          clientesService.getClientes({ business_type: 'AMBOS', is_active: true, limit: 500 })
        ]);
        const nalClients = Array.isArray(resNal.data) ? resNal.data : [];
        const bothClients = Array.isArray(resBoth.data) ? resBoth.data : [];
        const todos = [...nalClients, ...bothClients];
        const unicos = todos.filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i);
        setClientesNaturalAndLab(unicos.map(c => ({
          value: c.id.toString(),
          label: c.company_name || c.full_name || `Cliente #${c.id}`
        })));
      } catch (err) {
        console.error('Error loading clientes NaturalAndLab:', err);
        setClientesNaturalAndLab([]);
      } finally {
        setLoadingClientesNaturalAndLab(false);
      }
    };
    loadClientesNaturalAndLab();
  }, []);

  // Cargar ingredientes del producto cuando estamos editando
  useEffect(() => {
    const loadFormulacion = async () => {
      console.log('🔄 useEffect loadFormulacion ejecutado');
      console.log('   - isEditing:', isEditing);
      console.log('   - id:', id);
      console.log('   - producto.product_type:', producto.product_type);

      if (isEditing && id && producto.product_type) {
        const requiereFormulacion = ['BETEL', 'NATURALANDLAB', 'COTIZACIONES'].includes(producto.product_type);
        console.log('   - ¿Requiere formulación?', requiereFormulacion);

        if (requiereFormulacion) {
          console.log('✅ Cargando formulación para producto ID:', id);

          try {
            // Cargar ingredientes desde product_formulations
            const resultFormulacion = await productosService.getProductoFormulation(id);

            console.log('Formulación cargada:', resultFormulacion);

            if (resultFormulacion.success && resultFormulacion.data) {
              const formulations = resultFormulacion.data.formulations || [];
              // La fórmula viene en data.product.formula según el endpoint
              const formula = resultFormulacion.data.product?.formula || resultFormulacion.data.formula || producto.formula;

              console.log('   - Formulations recibidas:', formulations.length);
              console.log('   - Formula encontrada:', formula);
              console.log('   - Formula tiene container_product_id:', formula?.container_product_id);
              console.log('   - Formula tiene lid_product_id:', formula?.lid_product_id);
              console.log('   - Formula tiene formula_name:', formula?.formula_name);

              // Priorizar ingredientes de formula.ingredients (nueva estructura)
              let ingredientesFormateados = [];

              if (formula && formula.ingredients && formula.ingredients.length > 0) {
                console.log('   - Usando ingredientes de formula.ingredients:', formula.ingredients.length);
                ingredientesFormateados = formula.ingredients.map(ing => ({
                  ingredient_id: ing.ingredient_id,
                  ingredient_name: ing.ingredient_name || 'Ingrediente desconocido',
                  quantity_per_unit: parseFloat(ing.quantity_per_unit || 0),
                  unit: ing.unit || 'MG',
                  notes: ing.notes
                }));
              } else if (formulations.length > 0) {
                console.log('   - Usando ingredientes de product_formulations (legacy):', formulations.length);
                ingredientesFormateados = formulations.map(form => ({
                  ingredient_id: form.ingredient_id,
                  ingredient_name: form.ingredient?.name || 'Ingrediente desconocido',
                  quantity_per_unit: parseFloat(form.quantity || 0),
                  unit: form.unit || 'MG',
                  notes: form.notes
                }));
              }

              console.log('✅ Ingredientes formateados:', ingredientesFormateados);

              if (ingredientesFormateados.length > 0 || formula) {

                // Si el producto tiene fórmula (formula_id), cargar metadatos desde formula
                const formulacionInicial = {
                  ingredients: ingredientesFormateados
                };

                // Cargar metadatos de la fórmula si existen
                if (formula) {
                  console.log('📋 Cargando metadatos de fórmula:', {
                    id: formula.id,
                    formula_name: formula.formula_name,
                    container_product_id: formula.container_product_id,
                    lid_product_id: formula.lid_product_id,
                    ingredients_count: formula.ingredients?.length
                  });

                  // Guardar el ID de la fórmula para poder generar estimados
                  formulacionInicial.formula_id = formula.id;

                  formulacionInicial.company_name = formula.company_name || 'BETEL NATURAL';
                  formulacionInicial.formula_type = formula.formula_type || 'BETEL';
                  formulacionInicial.formula_name = formula.formula_name || '';

                  // IDs pueden ser 0, usar verificación explícita
                  formulacionInicial.container_product_id = formula.container_product_id !== null && formula.container_product_id !== undefined
                    ? formula.container_product_id
                    : '';
                  formulacionInicial.lid_product_id = formula.lid_product_id !== null && formula.lid_product_id !== undefined
                    ? formula.lid_product_id
                    : '';

                  formulacionInicial.quantity_per_container = formula.quantity_per_container ?? 90;
                  formulacionInicial.quantity_unit = formula.quantity_unit || 'UNIT';
                  formulacionInicial.capsule_type = formula.capsule_type || 'VEGETARIAN';
                  formulacionInicial.mixing_time_minutes = formula.mixing_time_minutes ?? 20;
                  formulacionInicial.best_use_date_years = formula.best_use_date_years || '';
                  formulacionInicial.label_printer = formula.label_printer || 'EN_BETEL';
                  formulacionInicial.box_size = formula.box_size || '16X13X13';
                  formulacionInicial.pieces_per_box = formula.pieces_per_box ?? 105;

                  // Boolean debe verificarse explícitamente
                  formulacionInicial.includes_silica = formula.includes_silica === true;

                  console.log('✅ Datos de fórmula cargados en formulacionInicial:', {
                    formula_name: formulacionInicial.formula_name,
                    container_product_id: formulacionInicial.container_product_id,
                    lid_product_id: formulacionInicial.lid_product_id
                  });
                }

                setFormulacionData(formulacionInicial);
                setOriginalFormulacionData(JSON.parse(JSON.stringify(formulacionInicial))); // Deep copy
                setHasFormulationChanges(false);
              } else {
                console.log('⚠️ No se encontraron ingredientes en la formulación');
              }
            } else {
              console.log('⚠️ Error al cargar formulación o sin datos:', resultFormulacion);
            }
          } catch (error) {
            console.error('Error cargando formulación:', error);
          }
        }
      }
    };

    loadFormulacion();
  }, [isEditing, id, producto.product_type]);

  // Recargar formulación cuando se cambia a la pestaña de formulación
  useEffect(() => {
    if (activeTab === 'formulacion' && isEditing && id && producto.product_type) {
      const requiereFormulacion = ['BETEL', 'NATURALANDLAB', 'COTIZACIONES'].includes(producto.product_type);

      console.log('📑 Cambio a pestaña de formulación');
      console.log('   - requiereFormulacion:', requiereFormulacion);
      console.log('   - formulacionData actual:', formulacionData);

      // Si no hay datos de fórmula cargados, recargar
      if (requiereFormulacion && (!formulacionData || Object.keys(formulacionData).length === 0)) {
        console.log('   - ⚠️ No hay datos de formulación, recargando...');
        // Llamar a la misma función de carga
        const loadFormulacionDelayed = async () => {
          try {
            const resultFormulacion = await productosService.getProductoFormulation(id);
            console.log('📦 Formulación recargada:', resultFormulacion);

            if (resultFormulacion.success && resultFormulacion.data) {
              const formulations = resultFormulacion.data.formulations || [];
              // La fórmula viene en data.product.formula según el endpoint
              const formula = resultFormulacion.data.product?.formula || resultFormulacion.data.formula || producto.formula;

              let ingredientesFormateados = [];

              if (formula && formula.ingredients && formula.ingredients.length > 0) {
                ingredientesFormateados = formula.ingredients.map(ing => ({
                  ingredient_id: ing.ingredient_id,
                  ingredient_name: ing.ingredient_name || 'Ingrediente desconocido',
                  quantity_per_unit: parseFloat(ing.quantity_per_unit || 0),
                  unit: ing.unit || 'MG',
                  notes: ing.notes
                }));
              } else if (formulations.length > 0) {
                ingredientesFormateados = formulations.map(form => ({
                  ingredient_id: form.ingredient_id,
                  ingredient_name: form.ingredient?.name || 'Ingrediente desconocido',
                  quantity_per_unit: parseFloat(form.quantity || 0),
                  unit: form.unit || 'MG',
                  notes: form.notes
                }));
              }

              const formulacionInicial = {
                ingredients: ingredientesFormateados
              };

              const formulaData = formula || producto.formula;
              if (formulaData) {
                console.log('📋 Cargando metadatos de fórmula en refresco:', formulaData);

                // Guardar el ID de la fórmula para poder generar estimados
                formulacionInicial.formula_id = formulaData.id;

                formulacionInicial.company_name = formulaData.company_name || 'BETEL NATURAL';
                formulacionInicial.formula_type = formulaData.formula_type || 'BETEL';
                formulacionInicial.formula_name = formulaData.formula_name || '';
                formulacionInicial.container_product_id = formulaData.container_product_id !== null && formulaData.container_product_id !== undefined
                  ? formulaData.container_product_id
                  : '';
                formulacionInicial.lid_product_id = formulaData.lid_product_id !== null && formulaData.lid_product_id !== undefined
                  ? formulaData.lid_product_id
                  : '';
                formulacionInicial.quantity_per_container = formulaData.quantity_per_container ?? 90;
                formulacionInicial.quantity_unit = formulaData.quantity_unit || 'UNIT';
                formulacionInicial.capsule_type = formulaData.capsule_type || 'VEGETARIAN';
                formulacionInicial.mixing_time_minutes = formulaData.mixing_time_minutes ?? 20;
                formulacionInicial.best_use_date_years = formulaData.best_use_date_years || '';
                formulacionInicial.label_printer = formulaData.label_printer || 'EN_BETEL';
                formulacionInicial.box_size = formulaData.box_size || '16X13X13';
                formulacionInicial.pieces_per_box = formulaData.pieces_per_box ?? 105;
                formulacionInicial.includes_silica = formulaData.includes_silica === true;

                console.log('✅ Datos de fórmula cargados en refresco:', formulacionInicial);
              }

              setFormulacionData(formulacionInicial);
              setOriginalFormulacionData(JSON.parse(JSON.stringify(formulacionInicial)));
            }
          } catch (error) {
            console.error('Error recargando formulación:', error);
          }
        };

        loadFormulacionDelayed();
      }
    }
  }, [activeTab, isEditing, id]);

  // Detectar cambios en formulación para habilitar botón de guardar
  useEffect(() => {
    if (isEditing && originalFormulacionData) {
      const formulacionActualStr = JSON.stringify(formulacionData);
      const formulacionOriginalStr = JSON.stringify(originalFormulacionData);
      const hayCambios = formulacionActualStr !== formulacionOriginalStr;

      if (hayCambios !== hasFormulationChanges) {
        setHasFormulationChanges(hayCambios);
        console.log('📝 Cambios en formulación detectados:', hayCambios);
      }
    }
  }, [formulacionData, originalFormulacionData, isEditing, hasFormulationChanges]);

  // Verificar si requiere formulación
  const requiereFormulacion = ['BETEL', 'NATURALANDLAB', 'COTIZACIONES'].includes(producto.product_type);

  // Loading state
  if (isLoading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner-single" style={{ width: '40px', height: '40px' }}></div>
        <div className="loading-message">
          {isEditing ? 'Cargando producto...' : 'Preparando formulario...'}
        </div>
      </div>
    );
  }

  return (
    <div className="producto-form-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={handleCancel}
          type="button"
        >
          <FiArrowLeft /> Volver a Productos
        </button>

        <h1 className="form-title">
          {isEditing ? 'EDITAR PRODUCTO' : 'NUEVO PRODUCTO'}
        </h1>

        <div className="form-actions">
          <button
            className="btn btn--outline"
            onClick={handleCancel}
            type="button"
            disabled={isSaving}
          >
            <FiX /> Cancelar
          </button>

          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSaving || (!hasChanges && !hasFormulationChanges && !hasImageChanges)}
            type="submit"
          >
            {isSaving ? (
              <>
                <div className="loading-spinner-single" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                Guardando...
              </>
            ) : (
              <>
                <FiSave /> {isEditing ? 'Actualizar' : 'Crear'} Producto
              </>
            )}
          </button>
        </div>
      </div>

      {/* Mensaje de éxito */}
      {successMessage && (
        <div className="alert alert--success" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Errores generales */}
      {(error || validationErrors.general) && (
        <div className="alert alert--danger">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error || validationErrors.general}
          </div>
        </div>
      )}

      {/* Errores de validación específicos */}
      {Object.keys(validationErrors).length > 1 || (Object.keys(validationErrors).length === 1 && !validationErrors.general) ? (
        <div className="alert alert--warning">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Errores de validación:</strong>
            <ul className="error-list">
              {Object.entries(validationErrors)
                .filter(([field]) => field !== 'general')
                .map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))
              }
            </ul>
          </div>
        </div>
      ) : null}

      {/* Tabs de navegación */}
      <div className="tabs-container" style={{ marginBottom: '24px' }}>
        <div className="tabs-horizontal">
          <button
            type="button"
            className={`tab-button ${activeTab === 'datos' ? 'active' : ''}`}
            onClick={() => setActiveTab('datos')}
          >
            <FiPackage /> Datos del Producto
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'formulacion' ? 'active' : ''} ${!requiereFormulacion ? 'disabled' : ''}`}
            onClick={() => requiereFormulacion && setActiveTab('formulacion')}
            disabled={!requiereFormulacion}
            title={!requiereFormulacion ? 'La formulación solo está disponible para productos BETEL NATURAL, NATURALANDLAB y COTIZACIONES' : ''}
          >
            <FiBarChart /> Formulación
          </button>
        </div>
      </div>

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="producto-form" style={{ display: activeTab === 'datos' ? 'block' : 'none' }}>
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Sección: Información Básica */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h2 className="section-title">Información Básica</h2>
              </div>

              <div className="form-group">
                <label className="form-label required">Nombre del Producto</label>
                <input
                  type="text"
                  className={`form-input ${validationErrors.name ? 'error' : ''}`}
                  value={producto.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Ej: Detoxsan Polvo 14oz"
                  maxLength={255}
                />
                {validationErrors.name && (
                  <span className="form-error">{validationErrors.name}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label required">Descripción</label>
                <textarea
                  className={`form-input ${validationErrors.description ? 'error' : ''}`}
                  value={producto.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Suplemento natural para desintoxicación..."
                  rows={3}
                />
                {validationErrors.description && (
                  <span className="form-error">{validationErrors.description}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">UPC</label>
                <input
                  type="text"
                  className={`form-input ${validationErrors.barcode ? 'error' : ''}`}
                  value={producto.barcode || ''}
                  onChange={(e) => handleInputChange('barcode', e.target.value)}
                  placeholder="Código UPC (se genera automáticamente si se deja vacío)"
                  maxLength={100}
                />
                {validationErrors.barcode && (
                  <span className="form-error">{validationErrors.barcode}</span>
                )}
              </div>

            </div>

            {/* Sección: Proveedores */}
            <div className="form-section">
              <div className="section-header">
                <FiTruck className="section-icon" />
                <h2 className="section-title">Proveedores</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Proveedores (Opcional)</label>

                {/* Buscador */}
                {(() => {
                  const selectedIds = producto.supplier_ids || [];
                  const available = proveedores.filter(p => !selectedIds.includes(parseInt(p.value)));
                  return (
                    <>
                      <select
                        className="form-input"
                        value=""
                        disabled={loadingProveedores}
                        onChange={(e) => {
                          if (!e.target.value) return;
                          const newId = parseInt(e.target.value);
                          if (!selectedIds.includes(newId)) {
                            handleInputChange('supplier_ids', [...selectedIds, newId]);
                          }
                          e.target.value = '';
                        }}
                      >
                        <option value="">
                          {loadingProveedores ? 'Cargando...' : `Agregar proveedor${available.length > 0 ? ` (${available.length} disponibles)` : ''}`}
                        </option>
                        {available.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>

                      {/* Tags de proveedores seleccionados */}
                      {selectedIds.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {selectedIds.map((id, idx) => {
                            const proveedor = proveedores.find(p => parseInt(p.value) === id);
                            if (!proveedor) return null;
                            return (
                              <span key={id} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                padding: '3px 10px 3px 10px',
                                background: idx === 0 ? '#f0e6ff' : '#f0f0f0',
                                border: `1px solid ${idx === 0 ? '#c89de8' : '#ccc'}`,
                                borderRadius: '20px',
                                fontSize: '13px',
                                color: idx === 0 ? '#5a1a7a' : '#444',
                              }}>
                                {idx === 0 && <span style={{ fontSize: '10px', fontWeight: '700', color: '#7B2D8E' }}>★ </span>}
                                {proveedor.label}
                                <button
                                  type="button"
                                  onClick={() => handleInputChange('supplier_ids', selectedIds.filter(sid => sid !== id))}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 4px', color: '#888', fontSize: '14px', lineHeight: 1 }}
                                  title="Quitar proveedor"
                                >×</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {selectedIds.length > 0 && (
                        <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                          ★ El primero es el proveedor principal
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Sección: Clasificación */}
            <div className="form-section">
              <div className="section-header">
                <FiInfo className="section-icon" />
                <h2 className="section-title">Clasificación del Producto</h2>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Departamento</label>
                  <select
                    className={`form-input ${validationErrors.product_type ? 'error' : ''}`}
                    value={producto.product_type || ''}
                    onChange={(e) => handleInputChange('product_type', e.target.value)}
                  >
                    <option value="">Seleccione departamento</option>
                    {tiposProducto.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                  {validationErrors.product_type && (
                    <span className="form-error">{validationErrors.product_type}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label required">Categoría</label>
                  <select
                    className={`form-input ${validationErrors.category ? 'error' : ''}`}
                    value={producto.category || ''}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    <option value="">Seleccione categoría</option>
                    {categorias.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                  {validationErrors.category && (
                    <span className="form-error">{validationErrors.category}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Estado</label>
                <select
                  className="form-input"
                  value={producto.status || 'ACTIVE'}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  {estados.map(estado => (
                    <option key={estado.value} value={estado.value}>{estado.label}</option>
                  ))}
                </select>
              </div>

              {/* Unidad de Medida Base */}
              <div className="form-group">
                <label className={`form-label ${producto.product_type === 'INGREDIENT' ? 'required' : ''}`}>
                  Unidad de Medida Base
                </label>
                <select
                  className={`form-input ${validationErrors.unit ? 'error' : ''}`}
                  value={producto.unit || 'UNIT'}
                  onChange={(e) => handleInputChange('unit', e.target.value)}
                >
                  {unidadesMedida.map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
                {validationErrors.unit && (
                  <span className="form-error">{validationErrors.unit}</span>
                )}
                <div className="form-help">
                  {producto.product_type === 'INGREDIENT'
                    ? '⚠ Importante: define cómo se compra/almacena este ingrediente. Se usa para convertir costos en formulaciones (ej: S/. 10/kg → S/. 0.00001/mg).'
                    : 'Unidad en la que se mide o compra este producto.'
                  }
                </div>
              </div>

              {/* Cliente NaturalAndLab si corresponde */}
              {producto.product_type === 'NATURALANDLAB' && (
                <div className="form-group">
                  <label className="form-label required">Cliente NaturalAndLab</label>
                  <select
                    className={`form-input ${validationErrors.naturalandlab_client_id ? 'error' : ''}`}
                    value={producto.naturalandlab_client_id || ''}
                    onChange={(e) => handleInputChange('naturalandlab_client_id', e.target.value)}
                    disabled={loadingClientesNaturalAndLab}
                  >
                    <option value="">
                      {loadingClientesNaturalAndLab ? 'Cargando clientes...' : 'Seleccione cliente'}
                    </option>
                    {clientesNaturalAndLab.map(cliente => (
                      <option key={cliente.value} value={cliente.value}>{cliente.label}</option>
                    ))}
                  </select>
                  {validationErrors.naturalandlab_client_id && (
                    <span className="form-error">{validationErrors.naturalandlab_client_id}</span>
                  )}
                  <div className="form-help">Seleccione el cliente para este producto personalizado</div>
                </div>
              )}
            </div>

            {/* Sección: Precios */}
            <div className="form-section">
              <div className="section-header">
                <h2 className="section-title">Estructura de Precios</h2>
              </div>

              <div className="precio-tiers">
                {loadingClientCategories ? (
                  <div className="loading-message">Cargando categorías de clientes...</div>
                ) : clientCategories.length === 0 ? (
                  <div className="alert alert--warning">
                    No hay categorías de clientes configuradas.
                    Por favor, configure las categorías de clientes primero.
                  </div>
                ) : (
                  <>
                    {precios.map((precio, index) => {
                      const costPrice = parseFloat(producto.cost_price) || 0;
                      const precioVal = parseFloat(precio.valor) || 0;
                      const precioMenorAlCosto = costPrice > 0 && precioVal > 0 && precioVal <= costPrice;
                      return (
                        <div key={precio.category_code || index} className="tier-item">
                          <div className="tier-content">
                            <div className="tier-name-display">
                              {precio.nombre}
                            </div>
                            <div className="tier-price">
                              <span className="currency">$</span>
                              <input
                                type="number"
                                className={`tier-input ${precioMenorAlCosto ? 'error' : ''}`}
                                value={precio.valor}
                                onChange={(e) => handlePrecioChange(index, 'valor', e.target.value)}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                          {precioMenorAlCosto && (
                            <span className="form-error" style={{ fontSize: '11px' }}>
                              Debe ser mayor al costo (${costPrice.toFixed(2)})
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}

                {producto.cost_price && parseFloat(producto.cost_price) > 0 && (
                  <div className="form-help" style={{ marginTop: '6px' }}>
                    Costo de referencia: <strong>${parseFloat(producto.cost_price).toFixed(2)}</strong> — Al menos un precio debe superarlo
                  </div>
                )}

                {validationErrors.precios && (
                  <div className="form-error">{validationErrors.precios}</div>
                )}
              </div>
            </div>

            {/* Sección: Información de Empaque */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h2 className="section-title">Información de Empaque</h2>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Piezas por Caja</label>
                  <input
                    type="number"
                    className="form-input"
                    value={producto.pieces_per_box || ''}
                    onChange={(e) => handleInputChange('pieces_per_box', e.target.value)}
                    placeholder="12"
                    min="1"
                  />
                  <div className="form-help">Cuántas unidades caben en una caja</div>
                </div>

                <div className="form-group">
                  <label className="form-label">Medidas de Caja (cm)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={producto.box_dimensions || ''}
                    onChange={(e) => handleInputChange('box_dimensions', e.target.value)}
                    placeholder="30x20x15"
                    maxLength={50}
                  />
                  <div className="form-help">Formato: largo x ancho x alto</div>
                </div>
              </div>
            </div>

          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Sección: Imágenes del Producto (Sistema Multi-Imagen) */}
            <div className="form-section">
              <ProductImageGallery
                productId={producto.id}
                onImagesChange={(images) => {
                  setHasImageChanges(true);
                }}
                onPendingFilesChange={(files) => {
                  setPendingImages(files);
                  setHasImageChanges(files.length > 0);
                }}
              />
            </div>
            {/* Sección: Costos y Precios */}
            <div className="form-section">
              <div className="section-header">
                <FiDollarSign className="section-icon" />
                <h2 className="section-title">Costos y Precios</h2>
              </div>

              <div className="form-group">
                <label className="form-label required">Costo del Producto</label>
                <div className="input-group">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    className={`form-input ${validationErrors.cost_price ? 'error' : ''}`}
                    value={producto.cost_price || ''}
                    onChange={(e) => handleInputChange('cost_price', e.target.value)}
                    placeholder="25000"
                    step="0.01"
                    min="0"
                  />
                </div>
                {validationErrors.cost_price && (
                  <span className="form-error">{validationErrors.cost_price}</span>
                )}
                <div className="form-help">
                  {requiereFormulacion ? "Se calculará automáticamente con la formulación" : "Costo de adquisición o producción"}
                </div>
              </div>
            </div>

            {/* Sección: Portal de Cliente */}
            <div className="form-section">
              <div className="section-header">
                <FiStar className="section-icon" />
                <h2 className="section-title">Portal de Cliente</h2>
              </div>

              <div className="form-group">
                <label className="form-radio-group">
                  <input
                    type="checkbox"
                    checked={producto.is_new || false}
                    onChange={(e) => handleInputChange('is_new', e.target.checked)}
                  />
                  <span className="checkbox-custom"></span>
                  Mostrar como producto nuevo en el portal
                </label>
                <div className="form-help">Los clientes verán este producto en la sección "Nuevos Productos"</div>
              </div>

              {producto.is_new && (
                <div className="form-group">
                  <label className="form-label">Nuevo hasta</label>
                  <input
                    type="date"
                    className="form-input"
                    value={producto.new_until ? new Date(producto.new_until).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleInputChange('new_until', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <div className="form-help">Fecha hasta la cual el producto se mostrará como nuevo</div>
                </div>
              )}
            </div>

            {/* Sección: Ofertas y Promociones */}
            <div className="form-section">
              <div className="section-header">
                <FiTag className="section-icon" />
                <h2 className="section-title">Ofertas y Promociones</h2>
              </div>

              <div className="form-group">
                <label className="form-radio-group">
                  <input
                    type="checkbox"
                    checked={producto.is_on_sale || false}
                    onChange={(e) => handleInputChange('is_on_sale', e.target.checked)}
                  />
                  <span className="checkbox-custom"></span>
                  Producto en oferta
                </label>
                <div className="form-help">Activar para mostrar este producto con precio de descuento</div>
              </div>

              {producto.is_on_sale && (
                <>
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Precio Regular</label>
                      <div className="input-group">
                        <span className="input-prefix">$</span>
                        <input
                          type="text"
                          className="form-input"
                          value={producto.sale_price || ''}
                          readOnly
                          disabled
                          style={{ background: '#f5f5f5' }}
                        />
                      </div>
                      <div className="form-help">Precio de venta normal del producto</div>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Precio con Descuento</label>
                      <div className="input-group">
                        <span className="input-prefix">$</span>
                        <input
                          type="number"
                          className={`form-input ${validationErrors.sale_discount_price ? 'error' : ''}`}
                          value={producto.sale_discount_price || ''}
                          onChange={(e) => handleInputChange('sale_discount_price', e.target.value)}
                          placeholder="35.00"
                          step="0.01"
                          min="0"
                          max={producto.sale_price}
                        />
                      </div>
                      {validationErrors.sale_discount_price && (
                        <span className="form-error">{validationErrors.sale_discount_price}</span>
                      )}
                    </div>
                  </div>

                  {/* Mostrar porcentaje de descuento calculado */}
                  {producto.sale_price && producto.sale_discount_price && (
                    <div className="alert alert--info" style={{ marginTop: '0.5rem' }}>
                      <div className="alert-content">
                        <strong>Descuento: </strong>
                        {Math.round(((parseFloat(producto.sale_price) - parseFloat(producto.sale_discount_price)) / parseFloat(producto.sale_price)) * 100)}% OFF
                        (Ahorro: ${(parseFloat(producto.sale_price) - parseFloat(producto.sale_discount_price)).toFixed(2)})
                      </div>
                    </div>
                  )}

                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Oferta inicia</label>
                      <input
                        type="date"
                        className="form-input"
                        value={producto.sale_starts ? new Date(producto.sale_starts).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleInputChange('sale_starts', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <div className="form-help">Fecha de inicio de la oferta</div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Oferta termina</label>
                      <input
                        type="date"
                        className="form-input"
                        value={producto.sale_ends ? new Date(producto.sale_ends).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleInputChange('sale_ends', e.target.value)}
                        min={producto.sale_starts || new Date().toISOString().split('T')[0]}
                      />
                      <div className="form-help">Fecha de fin de la oferta</div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Sección: Control de Inventario */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h2 className="section-title">Control de Inventario</h2>
              </div>

              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Stock Actual</label>
                  <input
                    type="number"
                    className="form-input"
                    value={producto.current_stock || ''}
                    onChange={(e) => handleInputChange('current_stock', e.target.value)}
                    placeholder="100"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Stock Mínimo</label>
                  <input
                    type="number"
                    className="form-input"
                    value={producto.min_stock || ''}
                    onChange={(e) => handleInputChange('min_stock', e.target.value)}
                    placeholder="20"
                    min="0"
                    step="0.01"
                  />
                  <div className="form-help">Alerta cuando llegue a este nivel</div>
                </div>

                <div className="form-group">
                  <label className="form-label">Stock Máximo</label>
                  <input
                    type="number"
                    className={`form-input ${validationErrors.max_stock ? 'error' : ''}`}
                    value={producto.max_stock || ''}
                    onChange={(e) => handleInputChange('max_stock', e.target.value)}
                    placeholder="500"
                    min="0"
                    step="0.01"
                  />
                  {validationErrors.max_stock && (
                    <span className="form-error">{validationErrors.max_stock}</span>
                  )}
                </div>
              </div>


              <div className="form-group">
                <label className="form-radio-group">
                  <input
                    type="checkbox"
                    checked={producto.track_inventory !== false}
                    onChange={(e) => handleInputChange('track_inventory', e.target.checked)}
                  />
                  <span className="checkbox-custom"></span>
                  Activar control de inventario
                </label>
                <div className="form-help">Desactivar para productos que no manejan stock</div>
              </div>
            </div>

            {/* Sección: Información Adicional */}
            <div className="form-section">
              <div className="section-header">
                <FiInfo className="section-icon" />
                <h2 className="section-title">Información Adicional</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Tiempo de Mejor Uso</label>
                <input
                  type="number"
                  className="form-input"
                  value={producto.expiration_days || ''}
                  onChange={(e) => handleInputChange('expiration_days', e.target.value)}
                  placeholder="730"
                  min="0"
                />
                <div className="form-help">Días de mejor uso del producto (730 = 2 años)</div>
              </div>

              <div className="form-group">
                <label className="form-label">Notas</label>
                <textarea
                  className="form-input"
                  value={producto.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Producto estrella de la línea..."
                  rows={3}
                />
              </div>
            </div>

          </div>
        </div>
      </form>

      {/* Contenido del tab de Formulación */}
      {activeTab === 'formulacion' && (
        <div className="formulacion-content">
          {requiereFormulacion ? (
            <>
              <div className="alert alert--info" style={{ marginBottom: '20px' }}>
                <FiBarChart className="alert-icon" />
                <div className="alert-content">
                  <strong>Formulación del Producto</strong>
                  <p style={{ marginTop: '8px' }}>
                    Configure la formulación del producto. Los datos se guardarán junto con la información básica al hacer clic en "Crear Producto" o "Actualizar Producto".
                  </p>
                </div>
              </div>

              {/* Botón Duplicar Formulación */}
              <div style={{ marginBottom: '20px', textAlign: 'right' }}>
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() => setIsDuplicarModalOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FiPackage /> DUPLICAR FORMULACIÓN
                </button>
              </div>

              {/* Componente de formulación */}
              <FormulacionFormSimple
                onFormulacionChange={(data) => {
                  console.log('🎯 FormulacionFormSimple notificó cambio:', data);

                  // Establecer company_name automáticamente según product_type
                  if (data && producto.product_type) {
                    const updatedData = { ...data };

                    // Mapear product_type a company_name
                    if (producto.product_type === 'BETEL') {
                      updatedData.company_name = 'BETEL NATURAL';
                      updatedData.formula_type = 'BETEL';
                    } else if (producto.product_type === 'NATURALANDLAB') {
                      updatedData.company_name = 'NATURALAND LAB';
                      updatedData.formula_type = 'NATURALANDLAB';
                    } else if (producto.product_type === 'COTIZACIONES') {
                      updatedData.company_name = 'COTIZACIONES';
                      updatedData.formula_type = 'COTIZACIONES';
                    }

                    console.log('✅ Company name establecido automáticamente:', updatedData.company_name);
                    setFormulacionData(updatedData);
                  } else {
                    setFormulacionData(data);
                  }
                }}
                initialData={formulacionData}
                productName={producto.name}
                productCategory={producto.category}
                validationErrors={validationErrors}
              />
            </>
          ) : (
            <div className="alert alert--warning">
              <FiAlertCircle className="alert-icon" />
              <div className="alert-content">
                <strong>Formulación no disponible</strong>
                <p>La formulación solo está disponible para productos de tipo BETEL NATURAL, NATURALANDLAB y COTIZACIONES.</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de duplicar formulación */}
      <DuplicarFormulacionModal
        isOpen={isDuplicarModalOpen}
        onClose={() => setIsDuplicarModalOpen(false)}
        onSelectFormulacion={(formulacion) => {
          console.log('Formulación recibida para duplicar:', formulacion);

          // Mapear los ingredientes al formato esperado por FormulacionFormSimple
          const ingredientesMapeados = (formulacion.ingredients || []).map(ing => ({
            ingredient_id: ing.ingredient_id || ing.id,
            ingredient_name: ing.ingredient_name || ing.name,
            quantity_per_unit: ing.quantity_per_unit || ing.quantity || 0,
            unit: ing.unit || 'MG',
            percentage: ing.percentage || null,
            notes: ing.notes || null
          }));

          // Solo duplicar campos específicos, NO duplicar:
          // 1. company_name, 2. product_name, 3. container_type, 4. lid_type,
          // 5. quantity_per_container, 6. capsule_type, 7. label_printer
          const formulacionMapeada = {
            // Mantener valores actuales del formulario (NO duplicar)
            company_name: formulacionData?.company_name || 'BETEL NATURAL',
            product_name: producto.name || '',
            container_type: formulacionData?.container_type || '',
            lid_type: formulacionData?.lid_type || '',
            quantity_per_container: formulacionData?.quantity_per_container || '',
            quantity_unit: formulacionData?.quantity_unit || 'UNIT',
            capsule_type: formulacionData?.capsule_type || 'VEGETARIAN',
            label_printer: formulacionData?.label_printer || '',

            // Duplicar estos campos de la formulación seleccionada
            formula_type: formulacion.formula_type || 'BETEL',
            formula_name: '',  // Dejar vacío para que el usuario ingrese manualmente
            mixing_time_minutes: formulacion.mixing_time_minutes || '',
            best_use_date_years: formulacion.best_use_date_years || '',
            box_size: formulacion.box_size || '',
            pieces_per_box: formulacion.pieces_per_box || '',
            includes_silica: formulacion.includes_silica || false,

            // SÍ duplicar ingredientes
            ingredients: ingredientesMapeados
          };

          console.log('Formulación mapeada:', formulacionMapeada);
          setFormulacionData(formulacionMapeada);
          setIsDuplicarModalOpen(false);
        }}
      />

      {/* Modal de confirmación para cambios no guardados */}
      <ConfirmModal
        isOpen={showUnsavedChangesModal}
        onCancel={() => setShowUnsavedChangesModal(false)}
        onConfirm={confirmCancel}
        title="¿Descartar cambios?"
        message="Tienes cambios sin guardar. ¿Estás seguro de que quieres salir sin guardar?"
        confirmText="Sí, descartar"
        cancelText="Continuar editando"
        type="warning"
      />
    </div>
  );
};

export default ProductoForm;